import React, { useRef, useState, useEffect, useCallback } from 'react';
import './leadershipIndividual.css';
import "@fontsource/poppins/400.css"; // Regular
import "@fontsource/poppins/500.css"; // Medium
import "@fontsource/poppins/600.css"; // Semi-Bold
import "@fontsource/poppins/700.css"; // Bold

import { downloadPdf, openPdf } from '../utils/pdfGenerator';
import PageCover from '../pages/pageCover';
import PageWelcome from '../pages/pageWelcome';
import FloatingActionButtons from '../../floatingActionButtons/floatingActionButtons';
import PageWelcome2 from '../pages/pageWelcome2';
import PageResumeIndividual from '../pages/pageResumeIndividual';
import PageResultsByDimensionIndividual from '../pages/pageResultsByDimensionIndividual';
import PageResultsByNpsIndividual from '../pages/pageResultsByNpsIndividual';
import PageTextualAnswersIndividual from '../pages/pageTextualAnswersIndividual';
import PageOportunitiesStrenghtsIndividual from '../pages/pageOportunitiesStrenghtsIndividual';
import PageFarewell from '../pages/pageFarewell';
import { Switch, Typography } from 'antd';
const { Text } = Typography;

const LeadershipIndividual = ({ totalResults, individualResult, selectedEvaluation, orgLogo }) => {
    const pdfRef = useRef();
    const [showNPS, setShowNPS] = useState(true);
    let pageNumber = 3;

    const getColor = (value) => {
        if (value <= selectedEvaluation.evl_max_red_light) return "#e53935";
        if (value <= selectedEvaluation.evl_max_yellow_light) return "#fbc02d";
        return "#43a047";
    };

    const prepareDimensionData = (dimension, key) => {
        const subdimensions = dimension?.subdimensions || [];

        // Buscar los promedios de organización desde totalResults
        const orgDimension = totalResults.average_by_dimension[key];

        let chartGroups = subdimensions.map((subdimension, index) => {
            // Buscar la misma subdimension en la parte "organizacional"
            const orgSub = orgDimension?.subdimensions?.find(
                (s) => s.id === subdimension.id
            );

            return {
                id: subdimension.id,
                question: subdimension.question,
                label: `Pregunta ${index + 1}`,
                average: subdimension.average,          // promedio individual (barra)
                orgAverage: orgSub?.average || 0,       // promedio organizacional (línea)
            };
        });

        return {
            key,
            title: dimension.title,
            dimension,
            chartGroups,
            globalAverage: dimension.average,
            orgGlobalAverage: orgDimension?.average || 0,
            color: getColor(dimension.average),
            type: 1,
        };
    };


    const preparedDimensions = [
        ...Object.entries(individualResult.average_by_dimension).map(
            ([key, dimension]) => ({
                key,
                ...prepareDimensionData(dimension, key),
            })
        ),
    ];

    // Función para dividir en bloques de máximo 20 respuestas
    const chunkArray = (array, size) => {
        const result = [];
        for (let i = 0; i < array.length; i += size) {
            result.push(array.slice(i, i + size));
        }
        return result;
    };

    // Preparar subdimensiones textuales como objetos individuales
    const preparedTextualDimensions = individualResult.general.flatMap((dimension, dimIndex) => {
        return (dimension.subdimensions || [])
            .filter((sub) => sub.type === "textual")
            .flatMap((sub, subIndex) => {
                const chunks = chunkArray(sub.text_answers || [], 15);
                return chunks.map((chunk, chunkIndex) => ({
                    key: `${dimIndex}-${sub.id || subIndex}-${chunkIndex}`, // 🔑 única
                    title: dimension.title,   // se repite aunque haya varias subdimensiones
                    question: sub.question,
                    answers: chunk,           // solo este bloque de hasta 20
                }));
            });
    });

    // Función para clasificar dimensiones
    const classifyDimensions = (average_by_dimension, selectedEvaluation) => {
        const thresholdGreen = selectedEvaluation.evl_max_yellow_light;
        const thresholdRed = selectedEvaluation.evl_max_red_light;

        const result = {
            fortalezas_conocidas: [],
            fortalezas_desconocidas: [],
            oportunidades_conocidas: [],
            oportunidades_desconocidas: [],
        };

        Object.values(average_by_dimension).forEach((dim) => {
            const { title, auto, another } = dim;

            if (auto > thresholdGreen && another > thresholdGreen) {
                result.fortalezas_conocidas.push(title);
            } else if (auto <= thresholdRed && another > thresholdGreen) {
                result.fortalezas_desconocidas.push(title);
            } else if (auto <= thresholdRed && another <= thresholdRed) {
                result.oportunidades_conocidas.push(title);
            } else if (auto > thresholdGreen && another <= thresholdRed) {
                result.oportunidades_desconocidas.push(title);
            }
        });

        return result;
    };

    const classifiedDimensions = classifyDimensions(individualResult.average_by_dimension, selectedEvaluation);

    const [pdfLoading, setPdfLoading] = useState(false);
    const [pdfProgress, setPdfProgress] = useState(null);
    const pdfFilename = `${selectedEvaluation.evl_title} - Reporte Individual - ${individualResult.first_name} ${individualResult.last_name}.pdf`;
    const onProgress = useCallback((current, total) => setPdfProgress({ current, total }), []);
    const handleDownloadPDF = async () => { setPdfLoading(true); await downloadPdf(pdfRef.current, { filename: pdfFilename, onProgress }); setPdfLoading(false); setPdfProgress(null); };
    const handleOpenPDF = async () => { setPdfLoading(true); await openPdf(pdfRef.current, { filename: pdfFilename, onProgress }); setPdfLoading(false); setPdfProgress(null); };

    return (
        <>
            {/* Control Switch */}
            <div className='sticky-switch' >
                <div className='sticky-switch-content'>
                    <Text style={{ marginBottom: 8, fontSize: 14, fontWeight: 500, }}>
                        Visualización resultados NPS
                    </Text>
                    <Switch
                        checked={showNPS}
                        onChange={setShowNPS}
                        checkedChildren={<span style={{ fontSize: 14 }}>Visualizar</span>}
                        unCheckedChildren={<span style={{ fontSize: 14 }}>No visualizar</span>}
                    // style={{ transform: 'scale(1.2)' }}
                    />
                </div>
            </div>


            <div ref={pdfRef} style={{ fontFamily: 'Poppins, sans-serif' }} className="pdf-content">
                <PageCover
                    reportTitle={`REPORTE INDIVIDUAL`}
                    reportSubtitle={selectedEvaluation.evl_title}
                    orgName={`${individualResult.first_name} ${individualResult.last_name}`}
                    orgRole={individualResult.job_title}
                    year={new Date(selectedEvaluation.evl_start_date).getFullYear()}
                    general={false}
                    orgLogo={orgLogo}
                />
                <PageWelcome selectedEvaluation={selectedEvaluation} pageNumber={1} />
                <PageWelcome2 selectedEvaluation={selectedEvaluation} pageNumber={2} />
                <PageResumeIndividual selectedEvaluation={selectedEvaluation} totalResults={totalResults} individualResult={individualResult} orgLogo={orgLogo} pageNumber={3} />

                {preparedDimensions.map((dimData) => {
                    let type = dimData.type;
                    let pages = [];
                    let totalPages = 0;
                    
                    if (type === 1) {
                        let subdimensions = dimData.dimension.subdimensions;
                        if (subdimensions.length > 10) {
                            pages = [pageNumber + 1, pageNumber + 2, pageNumber + 3];
                            totalPages = 3;
                        } else {
                            pages = [pageNumber + 1, pageNumber + 2];
                            totalPages = 2;
                        }
                    } else {
                        pages = [pageNumber + 1];
                        totalPages = 1;
                    }

                    pageNumber += totalPages;

                    return pages.map((page, index) => (
                        <PageResultsByDimensionIndividual
                            key={`${dimData.key}-page-${page}`}
                            selectedEvaluation={selectedEvaluation}
                            totalResults={totalResults}
                            individualResult={individualResult}
                            orgLogo={orgLogo}
                            pageNumber={page}
                            indexPage={index}
                            {...dimData}
                            totalQuestions={preparedDimensions.length}
                        />
                    ));
                })}
                
                <PageResultsByNpsIndividual
                    selectedEvaluation={selectedEvaluation}
                    individualResult={individualResult}
                    orgLogo={orgLogo}
                    pageNumber={pageNumber + 1}
                    title={individualResult.general[0].title}
                    subdimension={individualResult.general[0].subdimensions[0]}
                    showNPS={showNPS}
                />
                
                {preparedTextualDimensions.map((dimData, index) => {
                    // if (index == 0) {
                    //     pageNumber++;
                    // }
                    if (dimData.answers.length > 0) {
                        pageNumber++;
                        return (
                            <PageTextualAnswersIndividual
                                key={`TD${dimData.key}-${index}`}
                                individualResult={individualResult}
                                selectedEvaluation={selectedEvaluation}
                                orgLogo={orgLogo}
                                title={dimData.title}
                                question={`${dimData.question}`} // 👈 numeración automática
                                answers={dimData.answers}
                                pageNumber={pageNumber}
                            />
                        );
                    }
                    return null;
                })}

                <PageOportunitiesStrenghtsIndividual
                    individualResult={individualResult}
                    selectedEvaluation={selectedEvaluation}
                    orgLogo={orgLogo}
                    pageNumber={pageNumber + 1}
                    data={classifiedDimensions}
                />

                <PageFarewell selectedEvaluation={selectedEvaluation} pageNumber={pageNumber + 2} />
            </div>

            <FloatingActionButtons
                handleDownloadPDF={handleDownloadPDF}
                handleOpenPDF={handleOpenPDF}
                handleExportExcel={() => console.log("Exportando a Excel...")}
                pdfLoading={pdfLoading}
                pdfProgress={pdfProgress}
            />
        </>
    );
};

export default LeadershipIndividual;
